import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Box, Typography, Button, Link } from 'omaha-design-system';
import { useAuth } from '../../../hooks/useAuth';
import { setLeagueFilter } from '../store/slice';
import { DEFAULT_PROPS_LEAGUE, getPropsLeagueOptions } from '../data/leagueConfig';
import { getIsMobileApp } from '../../../utils/mobileApp';

interface LeagueOption {
  value: string;
  label: string;
  disabled: boolean;
}

const LEAGUE_OPTIONS: LeagueOption[] = getPropsLeagueOptions();

export const UnlockOverlay: React.FC = () => {
  const dispatch = useDispatch();
  const { login } = useAuth();
  const history = useHistory();
  const { league } = useParams<{ league: string }>();
  const selectedLeague = league?.toUpperCase() || DEFAULT_PROPS_LEAGUE;
  const isMobileApp = getIsMobileApp();

  const handleLeagueChange = (newLeague: string) => {
    // Update Redux state so subscription check uses the new league
    dispatch(setLeagueFilter(newLeague));
    history.push(`/props/${newLeague}`);
  };

  const handleSignIn = () => {
    // Redirect back to the current league after login
    login(`/props/${selectedLeague}`);
  };

  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        backgroundColor: '#0D0E10',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Stokastic Logo */}
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/images/longLogo.png`}
        alt="Stokastic"
        sx={{
          height: 40,
          width: 'auto',
          mb: 3,
        }}
      />

      {/* League Selector */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 4,
          p: 0.5,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
        }}
      >
        {LEAGUE_OPTIONS.map((option) => (
          <Button
            key={option.value}
            onClick={() => !option.disabled && handleLeagueChange(option.value)}
            disabled={option.disabled}
            sx={{
              minWidth: 60,
              py: 1,
              px: 2,
              borderRadius: '6px',
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              backgroundColor: selectedLeague === option.value ? '#005CCE' : 'transparent',
              color: selectedLeague === option.value ? 'white' : 'rgba(255, 255, 255, 0.6)',
              '&:hover': {
                backgroundColor: selectedLeague === option.value ? '#0048A8' : 'rgba(255, 255, 255, 0.1)',
              },
              '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {option.label}
          </Button>
        ))}
      </Box>

      {/* Headline */}
      <Typography
        sx={{
          fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
          fontWeight: 700,
          fontSize: { xs: 28, sm: 36 },
          color: 'white',
          textAlign: 'center',
          mb: 1.5,
        }}
      >
        Sign In to Access {selectedLeague} Props
      </Typography>

      {/* Supporting text */}
      <Typography
        sx={{
          fontFamily: '"Instrument Sans", sans-serif',
          fontWeight: 400,
          fontSize: 16,
          color: 'rgba(255, 255, 255, 0.6)',
          textAlign: 'center',
          mb: 4,
          maxWidth: 400,
        }}
      >
        {selectedLeague} player props, projections, and EV calculations to find the best bets
      </Typography>

      {/* Sign In Button */}
      <Button
        variant="contained"
        onClick={handleSignIn}
        sx={{
          width: { xs: '100%', sm: 300 },
          maxWidth: 300,
          py: 1.5,
          bgcolor: '#005CCE',
          color: 'white',
          fontFamily: '"Instrument Sans", sans-serif',
          fontWeight: 600,
          fontSize: 16,
          textTransform: 'none',
          borderRadius: '8px',
          mb: 3,
          '&:hover': {
            bgcolor: '#0048A8',
          },
        }}
      >
        Sign In
      </Button>

      {/* Need an Account link — hidden in mobile app (Apple Guideline 3.1.1) */}
      {!isMobileApp && (
        <Typography
          sx={{
            fontFamily: '"Instrument Sans", sans-serif',
            fontWeight: 400,
            fontSize: 14,
            color: 'rgba(255, 255, 255, 0.6)',
          }}
        >
          Need an Account?{' '}
          <Link
            href="https://stokastic.com/pricing"
            sx={{
              color: '#4DA3FF',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            View Plans
          </Link>
        </Typography>
      )}
    </Box>
  );
};

export default UnlockOverlay;
